/* ============================================================
   CUSTOM PDP VARIANT MEDIA TRANSITION

   Purpose:
   - Observe Horizon variant changes
   - Dim / blur gallery while new media loads
   - Wait for newly-rendered images to decode
   - Fade gallery back in

   Important:
   This does NOT replace Horizon's variant logic.
   ============================================================ */


class CustomPDPVariantTransition {
  constructor() {
    this.loadingClass =
      'custom-pdp-variant-loading';

    this.changeTimer =
      null;

    this.galleryObserver =
      null;

    this.activeRequest =
      0;

    /*
    * PDP elements whose value/state may change
    * when Horizon changes variant.
    *
    * Missing selectors are harmless.
    */

    this.transitionSelectors = [
        /* Price */
        'product-price',

        /* SKU */
        'product-sku',
        '[data-product-sku]',
        '[data-sku]',

        /* Inventory */
        'product-inventory',
        '[data-product-inventory]',
        '[data-inventory-status]',
        '.inventory-status',

        /* Wishlist */
        'xb-wishlist-button',

        /* Add to Cart */
        'add-to-cart-component',
        '.add-to-cart-button',

        /* Accelerated checkout / Buy Now */
        '.accelerated-checkout-block',
        '.shopify-payment-button',
        'shopify-accelerated-checkout',
        'shopify-buy-it-now-button',
        '.shopify-payment-button__button',

        /* Pickup */
        'local-pickup',
        '.product__pickup-availabilities'

        ];


    this.interactiveSelectors = [
        /* Wishlist */
        'xb-wishlist-button',

        /* Add to Cart */
        'add-to-cart-component',
        '.add-to-cart-button',

        /* Accelerated checkout / Buy Now */
        '.accelerated-checkout-block',
        '.shopify-payment-button',
        'shopify-accelerated-checkout',
        'shopify-buy-it-now-button',
        '.shopify-payment-button__button',

        /* Pickup */
        'local-pickup'

        ];

    this.init();
  }


  /* ========================================================
     INIT
     ======================================================== */

  init() {
    if (
      !this.isProductPage()
    ) {
      return;
    }


    this.handleChange =
      this.handleChange.bind(this);

    this.handleMutation =
      this.handleMutation.bind(this);


    /*
     * Capture variant changes from:
     * - radio buttons
     * - native selects
     * - Horizon variant picker controls
     */

    document.addEventListener(
      'change',
      this.handleChange,
      true
    );


    /*
     * Horizon may replace gallery markup after variant
     * selection, so observe the PDP product area.
     */

    this.observeProductDOM();

    this.registerTransitionTargets();
  }

    /* ========================================================
    REGISTER TRANSITION TARGETS
    ======================================================== */

    registerTransitionTargets() {

    /*
    * Restrict everything to the main PDP component.
    *
    * This prevents product cards, recommendations,
    * Quick Look or other forms from fading.
    */

    const productRoot =
        document.querySelector(
        'product-component'
        );


    if (!productRoot) {
        return;
    }


    /* --------------------------------------------------------
        VISUAL TARGETS
        -------------------------------------------------------- */

    this.transitionSelectors
        .forEach(
        selector => {

            productRoot
            .querySelectorAll(
                selector
            )
            .forEach(
                element => {

                element.classList.add(
                    'custom-pdp-variant-transition-target'
                );

                }
            );

        }
        );


    /* --------------------------------------------------------
        INTERACTIVE TARGETS
        -------------------------------------------------------- */

    this.interactiveSelectors
        .forEach(
        selector => {

            productRoot
            .querySelectorAll(
                selector
            )
            .forEach(
                element => {

                element.classList.add(
                    'custom-pdp-variant-interactive'
                );

                }
            );

        }
        );
    }


  /* ========================================================
     PRODUCT PAGE CHECK
     ======================================================== */

  isProductPage() {
    return Boolean(
      document.querySelector(
        'product-information, ' +
        'product-form-component, ' +
        'variant-picker'
      )
    );
  }


  /* ========================================================
     VARIANT CHANGE
     ======================================================== */

  handleChange(event) {
    const target =
      event.target;


    if (
      !(target instanceof HTMLElement)
    ) {
      return;
    }


    const isVariantControl =
      target.matches(
        'variant-picker input, ' +
        'variant-picker select, ' +
        '[data-option-value-id], ' +
        'select[name^="options"], ' +
        'input[name^="options"]'
      );


    if (!isVariantControl) {
      return;
    }


    /*
     * Ignore Quick Look.
     */

    if (
      target.closest(
        'custom-quick-look'
      )
    ) {
      return;
    }


    this.startLoading();
  }


  /* ========================================================
     START LOADING
     ======================================================== */

    startLoading() {
    this.activeRequest += 1;


    const requestId =
        this.activeRequest;


    /*
    * Re-scan before each transition.
    *
    * Horizon may have replaced some blocks since
    * the previous variant selection.
    */

    this.registerTransitionTargets();


    document.documentElement
        .classList.add(
        this.loadingClass
        );


    /*
    * Accessibility.
    *
    * Mark interactive components as being updated.
    */

    document
        .querySelectorAll(
        'product-component ' +
        '.custom-pdp-variant-interactive'
        )
        .forEach(
        element => {

            element.setAttribute(
            'aria-busy',
            'true'
            );

        }
        );


    /*
    * Safety timeout.
    *
    * The page must never remain visually stuck if
    * Horizon/network/image loading fails.
    */

    clearTimeout(
        this.changeTimer
    );


    this.changeTimer =
        window.setTimeout(
        () => {

            if (
            requestId ===
            this.activeRequest
            ) {

            this.finishLoading();

            }

        },
        5000
        );
    }


  /* ========================================================
     OBSERVE PRODUCT DOM
     ======================================================== */

  observeProductDOM() {
    const productRoot =
      document.querySelector(
        'main'
      ) ||
      document.body;


    this.galleryObserver =
      new MutationObserver(
        this.handleMutation
      );


    this.galleryObserver.observe(
      productRoot,
      {
        childList: true,
        subtree: true
      }
    );
  }


  /* ========================================================
     DOM MUTATION
     ======================================================== */

     handleMutation(mutations) {
        if (
            !document.documentElement
            .classList.contains(
                this.loadingClass
            )
        ) {
            return;
        }


        let mediaChanged =
            false;


        for (
            const mutation
            of mutations
        ) {

            if (
            mutation.type !==
            'childList'
            ) {
            continue;
            }


            const target =
            mutation.target;


            if (
            !(target instanceof Element)
            ) {
            continue;
            }


            if (
            target.matches(
                '.product-information__media'
            ) ||

            target.closest(
                '.product-information__media'
            ) ||

            target.querySelector?.(
                '.product-information__media'
            ) ||

            target.matches(
                'media-gallery'
            ) ||

            target.closest(
                'media-gallery'
            )
            ) {

            mediaChanged =
                true;

            break;
            }

        }


        if (!mediaChanged) {
            return;
        }


        clearTimeout(
            this.mediaMutationTimer
        );


        this.mediaMutationTimer =
            window.setTimeout(
            () => {

                /*
                * Horizon may have replaced price,
                * inventory, buttons etc. by now.
                *
                * Register the incoming elements so they
                * inherit the current loading state too.
                */

                this.registerTransitionTargets();


                requestAnimationFrame(
                () => {

                    this.waitForGalleryImages();

                }
                );

            },
            40
            );
        }

  /* ========================================================
     FIND CURRENT GALLERY
     ======================================================== */

  getGallery()
  {
    return (
        document.querySelector(
        '.product-information__media media-gallery'
        ) ||
        document.querySelector(
        '.product-information__media'
        )
    );
  }


  /* ========================================================
     WAIT FOR IMAGES
     ======================================================== */

  async waitForGalleryImages() {
    const requestId =
        this.activeRequest;


    const gallery =
        this.getGallery();


    if (!gallery) {
        this.finishLoading();

        return;
    }


    const images =
        [
        ...gallery.querySelectorAll(
            '.product-media img'
        )
        ];


    if (!images.length) {
        this.finishLoading();

        return;
    }


    /*
    * Horizon places the selected/primary media first
    * in the updated media state.
    *
    * We only need the first useful image decoded
    * before restoring the gallery.
    */

    const primaryImage =
        images.find(
        image =>
            image.src &&
            !image.closest('[hidden]')
        ) ||
        images[0];


    try {

        await this.waitForImage(
        primaryImage
        );

    } catch (error) {

        console.warn(
        'PDP variant image transition:',
        error
        );

    }


    /*
    * A newer variant was selected while this one
    * was loading. Do not finish the newer request.
    */

    if (
        requestId !==
        this.activeRequest
    ) {
        return;
    }


    requestAnimationFrame(
        () => {

        requestAnimationFrame(
            () => {

            this.finishLoading();

            }
        );

        }
    );
    }

  /* ========================================================
     WAIT FOR SINGLE IMAGE
     ======================================================== */

  waitForImage(image) {
    if (
      image.complete &&
      image.naturalWidth > 0
    ) {

      if (
        typeof image.decode ===
        'function'
      ) {

        return image.decode()
          .catch(() => {});

      }


      return Promise.resolve();
    }


    return new Promise(
      resolve => {

        const finish =
          () => {

            image.removeEventListener(
              'load',
              finish
            );

            image.removeEventListener(
              'error',
              finish
            );


            if (
              typeof image.decode ===
              'function' &&
              image.complete &&
              image.naturalWidth > 0
            ) {

              image.decode()
                .catch(() => {})
                .finally(resolve);

            } else {

              resolve();

            }

          };


        image.addEventListener(
          'load',
          finish,
          {
            once: true
          }
        );


        image.addEventListener(
          'error',
          finish,
          {
            once: true
          }
        );

      }
    );
  }


  /* ========================================================
     FINISH
     ======================================================== */

    finishLoading() {
    clearTimeout(
        this.changeTimer
    );


    clearTimeout(
        this.mediaMutationTimer
    );


    /*
    * Make sure newly-rendered Horizon components
    * have our enhancement classes before fade-in.
    */

    this.registerTransitionTargets();


    /*
    * Remove accessibility loading state.
    */

    document
        .querySelectorAll(
        'product-component ' +
        '.custom-pdp-variant-interactive'
        )
        .forEach(
        element => {

            element.removeAttribute(
            'aria-busy'
            );

        }
        );

    /*
    * Accelerated checkout buttons can remain keyboard
    * actionable even when their wrapper has pointer-events:none.
    */

    document
    .querySelectorAll(
        'product-component ' +
        '.shopify-payment-button__button, ' +
        'product-component ' +
        'shopify-buy-it-now-button button'
    )
    .forEach(button => {

        button.dataset.customPdpWasDisabled =
        String(button.disabled);

        button.disabled =
        true;

        button.setAttribute(
        'aria-busy',
        'true'
        );

    });
    
    /*
    * Restore accelerated checkout state exactly as Shopify
    * left it before our transition started.
    */

    document
    .querySelectorAll(
        'product-component ' +
        '.shopify-payment-button__button, ' +
        'product-component ' +
        'shopify-buy-it-now-button button'
    )
    .forEach(button => {

        const wasDisabled =
        button.dataset.customPdpWasDisabled ===
        'true';

        button.disabled =
        wasDisabled;

        button.removeAttribute(
        'aria-busy'
        );

        delete button.dataset.customPdpWasDisabled;

    });

    /*
    * Removing one global class causes gallery,
    * price, SKU, inventory, wishlist, ATC and
    * pickup availability to fade back together.
    */

    document.documentElement
        .classList.remove(
        this.loadingClass
        );
    }
}


/* ============================================================
   INITIALIZE
   ============================================================ */

if (
  document.readyState ===
  'loading'
) {

  document.addEventListener(
    'DOMContentLoaded',
    () => {

      new CustomPDPVariantTransition();

    },
    {
      once: true
    }
  );

} else {

  new CustomPDPVariantTransition();

}